import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type {
  AssetlogImportBody,
  AssetlogImportResult,
  AssetlogMode,
  AssetlogPlanResponse,
  BuildingDto,
  FloorChoice,
  LocationSource,
  RoomChoice,
} from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { useBuildingCache } from "../building/useBuilding";
import { ElementPicker } from "../elements/ElementPicker";

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");

const SOURCE_LABELS: Record<LocationSource, string> = {
  remembered: "eerder gekoppeld",
  found: "gevonden",
  new: "nieuw",
};

/** Excel-bestand als base64 (zoals de kennisbank-import); max. 10 MB. */
export async function readExcelFile(f: File): Promise<string> {
  if (f.size > 10 * 1024 * 1024) throw new Error("Dit bestand is te groot (max. 10 MB)");
  const buf = new Uint8Array(await f.arrayBuffer());
  let bin = "";
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(bin);
}

/** "building_[general]", "basement_[general]", "building": geen eigen plek in Assetlog. */
const placeLabel = (s: string) => (/\[general\]|^building$/i.test(s.trim()) ? "" : s.trim());

type Loc = { floor: FloorChoice; room: RoomChoice };
type Prod = { code: string | null; skip: boolean };

const choiceValue = (c: FloorChoice | RoomChoice) =>
  c.kind === "existing" ? `id:${c.id}` : c.kind === "new" ? `new:${c.name}` : "none";
const parseChoice = (v: string): FloorChoice =>
  v.startsWith("id:")
    ? { kind: "existing", id: v.slice(3) }
    : v.startsWith("new:")
      ? { kind: "new", name: v.slice(4) }
      : { kind: "none" };

/**
 * Venster "Inlezen Assetlog-Excel" (knowledge/Assetlog-import.md §4): de app stelt verdieping, ruimte
 * en indeling voor, de gebruiker past aan en bevestigt. Er wordt pas bij "Importeren" iets opgeslagen.
 */
export function AssetlogImportDialog({
  building: b,
  file,
  name,
  full,
  onClose,
}: {
  building: BuildingDto;
  file: string;
  name: string;
  full: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const cache = useBuildingCache(b.id);
  const plan = useQuery({
    queryKey: ["assetlog-plan", b.id, name, file.length],
    queryFn: () =>
      api<AssetlogPlanResponse>(`/buildings/${b.id}/assets/import/assetlog/plan`, { method: "POST", body: { file } }),
    retry: false,
    gcTime: 0,
  });
  const p = plan.data;
  const [mode, setMode] = useState<AssetlogMode>("assets");
  const [existing, setExisting] = useState<"update" | "skip">("update");
  const [locs, setLocs] = useState<Record<string, Loc>>({});
  const [prods, setProds] = useState<Record<string, Prod>>({});

  useEffect(() => {
    if (!p) return;
    setMode(p.format === "tasks" ? "assets_tasks" : "assets");
    setLocs(Object.fromEntries(p.locations.map((l) => [l.key, { floor: l.proposal.floor, room: l.proposal.room }])));
    setProds(Object.fromEntries(p.products.map((x) => [x.key, { code: x.code, skip: x.skip }])));
  }, [p]);

  const skipped = useMemo(
    () =>
      new Set(
        Object.entries(prods)
          .filter(([, v]) => v.skip)
          .map(([k]) => k),
      ),
    [prods],
  );
  const counts = useMemo(() => {
    const c = { new: 0, update: 0, skip: 0, error: 0 };
    if (!p) return c;
    for (const r of p.rows) {
      const st = r.baseStatus !== "error" && skipped.has(r.productKey) ? "skip" : r.baseStatus;
      c[st === "update" && existing === "skip" ? "skip" : st]++;
    }
    return c;
  }, [p, skipped, existing]);

  const run = useMutation({
    mutationFn: () => {
      const body: AssetlogImportBody = {
        file,
        mode,
        existing,
        locations: Object.entries(locs).map(([key, v]) => ({ key, ...v })),
        products: Object.entries(prods).map(([key, v]) => ({ key, ...v })),
      };
      return api<AssetlogImportResult>(`/buildings/${b.id}/assets/import/assetlog`, { method: "POST", body });
    },
    onSuccess: (r) => {
      void cache.refresh();
      const n = (k: number, one: string, many: string) => (k ? `${k} ${k === 1 ? one : many}` : "");
      const parts = [
        `${r.assets.created} nieuw`,
        `${r.assets.updated} bijgewerkt`,
        n(r.obligations.created + r.obligations.updated, "keuring", "keuringen"),
        n(r.replacements.created + r.replacements.updated, "vervanging", "vervangingen"),
      ].filter(Boolean);
      toast(
        `Assetlog ingelezen: ${parts.join(", ")}.${r.unplaced ? ` ${r.unplaced} assets hebben nog geen plek op de tekening.` : ""}`,
      );
      onClose();
    },
    onError: (e) => toast(errMsg(e)),
  });

  const unclassified = p ? p.products.filter((x) => !prods[x.key]?.code && !prods[x.key]?.skip).length : 0;
  // Wat aandacht nodig heeft eerst: producten zonder indeling.
  const productOrder = p
    ? [...p.products].sort(
        (a, c) =>
          Number(!!prods[a.key]?.code || !!prods[a.key]?.skip) - Number(!!prods[c.key]?.code || !!prods[c.key]?.skip),
      )
    : [];
  const locSummary = p
    ? (["remembered", "found", "new"] as const)
        .map((k) => [k, p.locations.filter((l) => l.proposal.source === k).length] as const)
        .filter(([, n]) => n)
        .map(([k, n]) => `${n} ${SOURCE_LABELS[k]}`)
        .join(" · ")
    : "";
  // Per Assetlog-gebouw en -verdieping één groep; de ruimtes eronder.
  const groups = useMemo(() => {
    const m = new Map<
      string,
      { key: string; building: string; label: string; items: AssetlogPlanResponse["locations"] }
    >();
    for (const l of p?.locations ?? []) {
      const key = `${l.building}/${l.floor}`;
      const g = m.get(key) ?? { key, building: l.building, label: placeLabel(l.floor) || "Hele gebouw", items: [] };
      g.items.push(l);
      m.set(key, g);
    }
    return [...m.values()];
  }, [p]);
  const newRoomOf = (l: AssetlogPlanResponse["locations"][number]) =>
    l.proposal.room.kind === "new" ? l.proposal.room.name : placeLabel(l.room) || null;
  const floorsOf = (fid: FloorChoice) => (fid.kind === "existing" ? b.rooms.filter((r) => r.floorId === fid.id) : []);
  const total = counts.new + counts.update;

  return (
    <Modal
      title="Inlezen Assetlog-Excel"
      onClose={onClose}
      wide
      busy={run.isPending}
      footer={
        <>
          {p && (
            <span className="muted small al-foot" aria-live="polite" data-testid="assetlog-counts">
              {counts.new} nieuw · {counts.update} bijwerken
              {counts.skip ? ` · ${counts.skip} overslaan` : ""}
              {counts.error ? ` · ${counts.error} fout` : ""}
            </span>
          )}
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={!p || total === 0 || run.isPending}
            onClick={() => run.mutate()}
            data-testid="assetlog-run"
          >
            {run.isPending ? "Bezig…" : `Importeren (${total})`}
          </button>
        </>
      }
    >
      <p className="muted small">{name}</p>
      {plan.isPending ? (
        <p className="muted">Bestand lezen…</p>
      ) : plan.error ? (
        <p role="alert" className="lib-warn">
          {errMsg(plan.error)}
        </p>
      ) : p ? (
        <div className="stack al-import">
          <p data-testid="assetlog-summary">
            {p.rows.length} assets
            {p.format === "tasks" ? ` met ${p.fileRows} taken` : ""} op {p.locations.length} locaties. Er is nog niets
            opgeslagen.
          </p>
          {p.rows.some((r) => r.status === "error") && (
            <ul className="small al-errors" role="alert">
              {p.rows
                .filter((r) => r.status === "error")
                .map((r) => (
                  <li key={r.code} className="lib-warn">
                    Rij {r.row}, {r.code} {r.name}: {r.message}
                  </li>
                ))}
            </ul>
          )}
          {p.problems.length > 0 && (
            <p className="muted small">{p.problems.length} rijen in het bestand konden niet worden gelezen.</p>
          )}

          <fieldset className="obresult">
            <legend className="label">Wat wil je inlezen?</legend>
            <label className="obres">
              <input type="radio" name="al-mode" checked={mode === "assets"} onChange={() => setMode("assets")} />
              <span>Alleen assets</span>
            </label>
            <label className="obres">
              <input
                type="radio"
                name="al-mode"
                checked={mode === "assets_tasks"}
                disabled={p.format !== "tasks"}
                onChange={() => setMode("assets_tasks")}
              />
              <span>
                Assets met keuringen en vervangingen
                {p.format !== "tasks" && (
                  <span className="muted small block">
                    Dit bestand heeft geen taken. Gebruik de takenexport (Tasks+asset) van Assetlog.
                  </span>
                )}
                {p.format === "tasks" && p.tasks && (
                  <span className="muted small block">
                    {p.tasks.obligations} {p.tasks.obligations === 1 ? "keuring" : "keuringen"} en{" "}
                    {p.tasks.replacements} {p.tasks.replacements === 1 ? "vervanging" : "vervangingen"}
                    {p.tasks.mergedReplacements
                      ? `; ${p.tasks.mergedReplacements} dubbele vervangingen vallen weg (één per asset, de duurste blijft)`
                      : ""}
                    {p.tasks.withoutDate ? `. ${p.tasks.withoutDate} keuringen hebben nog geen datum` : ""}
                  </span>
                )}
              </span>
            </label>
          </fieldset>

          {p.rows.some((r) => r.status === "update") && (
            <label className="field">
              <span className="label">Assets die al bestaan</span>
              <select value={existing} onChange={(e) => setExisting(e.target.value as "update" | "skip")}>
                <option value="update">Bestaande bijwerken</option>
                <option value="skip">Bestaande overslaan</option>
              </select>
            </label>
          )}

          <details className="al-sec" open={groups.length <= 4} data-testid="assetlog-locations">
            <summary>
              <span className="label">Locaties koppelen</span>
              <span className="muted small">
                {groups.length} {groups.length === 1 ? "verdieping" : "verdiepingen"} · {p.locations.length} ruimtes ·{" "}
                {locSummary}
              </span>
            </summary>
            <ul className="al-list">
              {groups.map((g) => {
                const first = locs[g.items[0]!.key];
                if (!first) return null;
                const newFloor = g.items
                  .map((l) => (l.proposal.floor.kind === "new" ? l.proposal.floor.name : null))
                  .find(Boolean);
                const assets = g.items.reduce((n, l) => n + l.assets, 0);
                // Eén keuze voor de verdieping geldt voor alle ruimtes van deze Assetlog-verdieping.
                const setFloor = (floor: FloorChoice) =>
                  setLocs((s) => {
                    const next = { ...s };
                    for (const l of g.items) {
                      const room = newRoomOf(l);
                      next[l.key] = {
                        floor,
                        room: floor.kind === "none" || !room ? { kind: "none" } : { kind: "new", name: room },
                      };
                    }
                    return next;
                  });
                return (
                  <li key={g.key} className="al-group">
                    <div className="al-ghead">
                      <div className="al-src">
                        <strong>{g.label}</strong>
                        <span className="muted small block">
                          {g.building} · {assets} {assets === 1 ? "asset" : "assets"}
                        </span>
                      </div>
                      <label className="field">
                        <span className="label">Verdieping</span>
                        <select
                          aria-label={`Verdieping voor ${g.label}`}
                          value={choiceValue(first.floor)}
                          onChange={(e) => setFloor(parseChoice(e.target.value))}
                        >
                          <option value="none">Geen verdieping</option>
                          {b.floors.map((f) => (
                            <option key={f.id} value={`id:${f.id}`}>
                              {f.name}
                            </option>
                          ))}
                          {newFloor && <option value={`new:${newFloor}`}>Nieuw: {newFloor}</option>}
                        </select>
                      </label>
                    </div>
                    {first.floor.kind !== "none" && (
                      <ul className="al-rooms">
                        {g.items.map((l) => {
                          const v = locs[l.key];
                          if (!v) return null;
                          const room = newRoomOf(l);
                          return (
                            <li key={l.key} className="al-room">
                              <span className="al-src">
                                {placeLabel(l.room) || "Algemeen"}
                                <span className="muted small">
                                  {" "}
                                  · {l.assets}{" "}
                                  <span className={`chip small al-${l.proposal.source}`}>
                                    {SOURCE_LABELS[l.proposal.source]}
                                  </span>
                                </span>
                              </span>
                              <select
                                aria-label={`Ruimte voor ${placeLabel(l.room) || g.label}`}
                                value={choiceValue(v.room)}
                                onChange={(e) =>
                                  setLocs((s) => ({
                                    ...s,
                                    [l.key]: { ...v, room: parseChoice(e.target.value) as RoomChoice },
                                  }))
                                }
                              >
                                <option value="none">Geen ruimte</option>
                                {floorsOf(v.floor).map((r) => (
                                  <option key={r.id} value={`id:${r.id}`}>
                                    {r.name}
                                  </option>
                                ))}
                                {room && <option value={`new:${room}`}>Nieuw: {room}</option>}
                              </select>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </details>

          <details className="al-sec" open={p.products.length <= 8 || unclassified > 0} data-testid="assetlog-products">
            <summary>
              <span className="label">Indeling controleren</span>
              <span className="muted small">
                {p.products.length} {p.products.length === 1 ? "product" : "producten"}
                {unclassified ? ` · ${unclassified} zonder indeling` : " · alles ingedeeld"}
              </span>
            </summary>
            <ul className="al-list">
              {productOrder.map((x) => {
                const v = prods[x.key];
                if (!v) return null;
                return (
                  <li key={x.key} className={`al-item al-prod${!v.code && !v.skip ? " al-attn" : ""}`}>
                    <div className="al-src">
                      <strong>{x.product}</strong>
                      <span className="muted small block">
                        {x.planonCode ? `NL-SfB ${x.planonCode} · ` : ""}
                        {x.assets} {x.assets === 1 ? "asset" : "assets"}
                      </span>
                      <label className="al-skip small">
                        <input
                          type="checkbox"
                          checked={v.skip}
                          onChange={(e) => setProds((s) => ({ ...s, [x.key]: { ...v, skip: e.target.checked } }))}
                        />
                        <span>Overslaan</span>
                      </label>
                    </div>
                    {!v.skip && (
                      <div className="al-picker">
                        <ElementPicker
                          value={v.code}
                          full={full}
                          onChange={(code) => setProds((s) => ({ ...s, [x.key]: { ...v, code } }))}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </details>
        </div>
      ) : null}
    </Modal>
  );
}
